<template>
  <div class="c-cart-product-actions">
    <div class="c-cart-product-actions__action">
      <div
        v-if="unavailableLabel"
        class="c-cart-product-actions__unavailable-label"
      >
        {{ unavailableLabel }}
      </div>

      <div v-else-if="isQuantityChangeable">
        <label class="c-cart-product-actions__label-quantity">数量</label>
        <input
          type="number"
          inputmode="numeric"
          class="c-cart-product-actions__quantity-input"
          min="1"
          :max="purchaseLimit"
          @change="onQuantityChanged"
          v-model="quantity"
        />
      </div>

      <div v-else class="c-cart-product-actions__label-quantity">
        数量: {{ quantity }}
      </div>
    </div>

    <div v-if="isRemovable" class="c-cart-product-actions__action">
      <button
        class="c-cart-product-actions__button-remove"
        @click="onRemoveClicked"
        :disabled="isProcessingGlobal"
      >
        削除
      </button>
    </div>

    <div
      class="c-cart-product-actions__action"
      v-if="isInPurchaseLater || isPurchaseLaterAvailable"
    >
      <button
        class="c-cart-product-actions__button-purchase-later"
        :class="{ outline: !isInPurchaseLater }"
        @click="onButtonClick"
        :disabled="isButtonDisabled"
        v-text="text"
      ></button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
let timer = null

export default {
  name: 'CartProductActions',
  props: {
    customerId: { type: String },
    endpointPurchaseLater: { type: String, required: true, default: '' },
    handle: { type: String },
    hmac: { type: String },
    isInPurchaseLater: { type: Boolean, default: false },
    isPurchaseLaterAvailable: { type: Boolean, default: false },
    isRemovable: { type: Boolean, default: true },
    isQuantityChangeable: { type: Boolean, default: false },
    optionRef: { type: String, default: '' },
    purchaseLimit: { type: Number, default: Number.MAX_SAFE_INTEGER },
    quantityInCart: { type: Number, default: 1 },
    unavailableLabel: { type: String, default: '' },
    variantId: { type: String, required: true, default: '' },
    index: { type: Number },
    isProcessingGlobal: { type: Boolean, default: false },
    updateCartItemsCallback: { type: Function, required: true },
  },
  data() {
    return {
      quantity: this.quantityInCart || 1,
    }
  },
  computed: {
    isButtonDisabled() {
      return (
        this.isProcessingGlobal ||
        this.unavailableLabel === 'SOLD OUT' ||
        (this.isInPurchaseLater && this.unavailableLabel === '入荷待ち受付中')
      )
    },
    text() {
      return this.isInPurchaseLater ? 'カートに戻す' : 'あとで買う'
    },
  },
  methods: {
    async addToPurchaseLater() {
      try {
        await axios.post(this.endpointPurchaseLater, {
          h: this.hmac,
          customerId: this.customerId,
          product: {
            handle: this.handle,
            variant_id: this.variantId,
            quantity: this.quantity,
          },
        })
      } catch (error) {
        console.error(error)
        throw error
      }
    },

    async onButtonClick() {
      if (this.isProcessingGlobal) return
      this.$emit('processing-change', true)
      try {
        if (this.isInPurchaseLater) {
          await this.updateCartItems({ quantity: this.quantity })
          await this.removeFromPurchaseLater()
          window.alert(
            `カートに商品を${this.quantity}点戻しました。2点以上ご購入の際は、数量を再度入力してください。`
          )
        } else {
          await this.updateCartItems({ quantity: 0 })
          await this.addToPurchaseLater()
          window.alert(
            'あとで買うリストに商品を保存しました。数量は保存されておりませんので、カートに戻してから再度入力してください。\nまた、在庫の確約ではありませんので、あらかじめご注意ください。'
          )
        }
        window.location.reload()
      } catch (error) {
        console.error(error)
        alert(
          '申し訳ございませんが、アイテムの操作中にエラーが発生しました。しばらくしてからもう一度お試しください。'
        )
      } finally {
        this.$emit('processing-change', false)
      }
    },
    async onRemoveClicked() {
      if (this.isProcessingGlobal) return

      if (
        confirm(
          `商品を${
            this.isInPurchaseLater ? 'あとで買う' : 'カート'
          }から削除します。よろしいですか？`
        )
      ) {
        this.$emit('processing-change', true)
        try {
          if (this.isInPurchaseLater) {
            await this.removeFromPurchaseLater()
          } else {
            await this.updateCartItems({ quantity: 0 })
          }
          window.location.reload()
        } catch (error) {
          console.error(error)
          alert(
            '申し訳ございませんが、アイテムの操作中にエラーが発生しました。しばらくしてからもう一度お試しください。'
          )
        } finally {
          this.$emit('processing-change', false)
        }
      }
    },
    onQuantityChanged() {
      if (this.isProcessingGlobal) return
      this.$emit('processing-change', true)
      clearTimeout(timer)
      timer = setTimeout(async () => {
        try {
          if (this.quantity === '') {
            throw new Error('数量は半角数字で入力してください。')
          } else if (this.quantity > this.purchaseLimit) {
            throw new Error('購入できる上限数を超えています。')
          } else if (this.quantity < 1) {
            throw new Error('数量を1以上にしてください。')
          }
          await this.updateCartItems()
          window.location.reload()
        } catch (error) {
          const message =
            error.message ||
            '申し訳ございませんが、数量変更中にエラーが発生しました。しばらくしてからもう一度お試しください。'
          alert(message)
          console.error(error)
          this.quantity = this.quantityInCart
          clearTimeout(timer)
        } finally {
          this.$emit('processing-change', false)
        }
      }, 500)
    },
    async removeFromPurchaseLater() {
      try {
        await axios.delete(this.endpointPurchaseLater, {
          data: {
            h: this.hmac,
            customerId: this.customerId,
            product: {
              handle: this.handle,
              variant_id: this.variantId,
            },
          },
        })
      } catch (error) {}
    },
    async updateCartItems({ quantity } = {}) {
      return await this.updateCartItemsCallback({
        optionRef: this.optionRef,
        quantity: quantity !== undefined ? quantity : this.quantity,
        variantId: this.variantId,
        index: this.index,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@use '~/scss/generic/variables';
@use '~/scss/generic/mixins';
@use '~/scss/modules/button';

.c-cart-product-actions {
  display: flex;
  gap: 10px;

  @include mixins.screen-desktop {
    display: flex;
    gap: 15px;
  }

  &__action {
    align-items: center;
    display: flex;
  }

  &__unavailable-label {
    align-items: center;
    color: variables.$color-red;
    display: flex;
    font-size: 15px;
  }

  &__label-quantity {
    font-size: 15px;
    white-space: nowrap;
  }

  &__quantity-input {
    appearance: none;
    background-color: #fff;
    border: 1px solid variables.$color-text-gray-2;
    border-radius: 5px;
    font-size: 16px;
    padding: 2px 5px 2px 10px;
    margin-left: 5px;
    width: 70px;
  }

  &__button-remove {
    color: variables.$color-accent;
    font-size: 15px;
    white-space: nowrap;
  }

  &__button-purchase-later {
    @extend .m-button;
    font-size: 10px;
    padding: 5px 15px;
    white-space: nowrap;
  }
}
</style>
